import { Flex, Link, LoadingIcon, Message } from "basis";
import { Error } from "components/Error/Error";
import { Otp } from "components/Otp/Otp";
import { useAccounts } from "library/accounts";
import { enhanceAccounts } from "library/accounts/enhanceAccounts";
import { sortAccounts } from "library/accounts/sortAccounts";
import { getAddresses } from "library/api";
import { useProducts } from "library/hooks/useProducts";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { ParsedAccountsAddressesResponse } from "../types";
import { parseAddressesResponse } from "../utils";
import { AccountsList } from "./AccountsList";

export const Landing = () => {
  const productsInfo = useProducts();
  const accountsInfo = useAccounts({
    fullCreditCardsInfo: true,
    fullPersonalLoansInfo: true, // even though PLs are not supported, we need to fetch the PL to get its account number for display
  });

  const accountsToShow = useMemo(() => {
    if (!accountsInfo.data || !productsInfo.data) {
      return [];
    }

    const enhancedAccounts = enhanceAccounts(
      [...accountsInfo.data.credit_cards, ...accountsInfo.data.personal_loans],
      productsInfo.data,
    );

    return sortAccounts(enhancedAccounts).filter(
      (account) => account.status !== "CLOSED",
    );
  }, [accountsInfo.data, productsInfo.data]);

  const tokens = useMemo(
    () =>
      accountsToShow.reduce<string[]>((acc, account) => {
        if (account.type === "CREDIT_CARD" && account.credit_card?.token) {
          acc.push(account.credit_card?.token);
        }

        return acc;
      }, []),
    [accountsToShow],
  );

  const shouldFetchAddresses =
    accountsInfo.data !== undefined &&
    productsInfo.data !== undefined &&
    tokens.length > 0;

  const addressesInfo = useQuery<ParsedAccountsAddressesResponse>(
    ["addresses", tokens],
    async () => {
      const result = await getAddresses(tokens);
      return parseAddressesResponse(result.data, accountsToShow);
    },
    {
      enabled: shouldFetchAddresses,
    },
  );

  const isLoading =
    addressesInfo.isLoading || accountsInfo.isLoading || productsInfo.isLoading;

  if (addressesInfo.isError || accountsInfo.isError || productsInfo.isError) {
    return <Error />;
  }

  if (isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  if (accountsToShow.length === 0 && !isLoading) {
    return (
      <Message
        severity="info-or-minor"
        bg="secondary.pink.t30"
        title="No accounts linked"
      >
        You do not currently have any accounts linked to the Latitude Service
        Centre.{" "}
        <Link href="/link-account" newTab={false}>
          Link account
        </Link>
      </Message>
    );
  }

  const token =
    accountsInfo.data?.credit_cards[0]?.credit_card?.token ||
    accountsInfo.data?.personal_loans[0]?.credit_card?.token;

  if (
    token &&
    addressesInfo.data?.otpNeeded &&
    addressesInfo.data?.createSessionUrl
  ) {
    return (
      <Otp
        createSessionUrl={addressesInfo.data.createSessionUrl}
        token={token}
        errorFooter={
          <Link appearance="primary-button" width="256" href="/" newTab={false}>
            Overview
          </Link>
        }
        onSuccess={async () => {
          await addressesInfo.refetch();
        }}
      />
    );
  }

  return (
    <AccountsList
      accountsWithPersonalDetails={
        addressesInfo.data?.accountsWithPersonalDetails ?? []
      }
    />
  );
};
